import * as React from "react";
import { FixedCommentMutation, TestAcceptabilityMutation, OverreadStatusEnum, Permission, OscillometryAnalysisSet, SessionMetadata, OverreadStatusIdEnum } from "../../misc/api/apiTypes";
import { OscillometryResultsTable } from "./OscillometryResultsTable";
import { RouteComponentProps } from "react-router";
import { useFixedComments, ToggleSessionFixedCommentsVisibilityAction } from "../Shared/UseFixedComments";
import FileAttachmentsTable from "../../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "../../file-attachments/models";
import NotesPanel from "../Shared/NotesPanel";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import SessionMetadataTable from "../Shared/SessionMetadataTable";
import AcceptabilityDashboard from "../Shared/AcceptabilityDashboard";
import { useAcceptability, LoadingBestValuesAction, UpdateBestTestAction, UpdateRepeatabilityAction, DoneLoadingBestValuesAction } from '../Shared/UseAcceptability';
import { MainState } from "../../Core/reducers";
import { connect } from "react-redux";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { OscillometryResults } from "../Shared/PFTAPITypes";
import { OscillometryAnalysisSetTypeEnum } from "../../misc/api/ApiOscillometryAnalysisSetTypeEnum";
import { WebPortalClient, SessionNote } from "webportalclient";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { GoBackToList } from "../Shared/AnalysisHelpers";

export interface OscillometryAnalysisScreenProps {
}

export interface OscillometryAnalysisScreenRouteProps
    extends RouteComponentProps<ExpectedMatchParams>,
    OscillometryAnalysisScreenProps { }

enum ViewState {
    View = "view",
    Overread = "overread"
}

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: ViewState;
}

export const OscillometryAnalysisScreen = ({ match, history }: OscillometryAnalysisScreenRouteProps) => {
    const { analysisSetId, viewState } = match.params;

    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [analysisSet, setAnalysisSet] = React.useState<OscillometryResults>(null);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const [isAnalysisRejected, setIsAnalysisRejected] = React.useState<boolean>(false);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    const { currentUserPermissions } = useSecurityContext();

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            if (viewState === ViewState.Overread) {
                setIsViewOnly(false);
                await legacyClient.startOscillometryOverreading(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const results = await legacyClient.getOscillometryAnalysisSetResults(analysisSetId);
            setAnalysisSet(results);

            const sessionMetadata = await legacyClient.getOscillometrySession(results.SessionId);
            setSessionMetadata(sessionMetadata);

            await fetchSessionNotes(results.SessionId);

            const isRejected = sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id === OverreadStatusIdEnum.Rejected;
            setIsAnalysisRejected(isRejected);
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);

    const [shouldValidateFixedComments, setShouldValidateFixedComments] = React.useState(false);

    const [fixedCommentState, fixedCommentDispatch] = useFixedComments(analysisSet, SessionTypes.Oscillometry);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(analysisSet);

    React.useEffect(() => {

        const updateBestTestAndRepeatability = async () => {
            if (acceptabilityState.spirometryResults.TestResults.length) {

                acceptabilityDispatch(LoadingBestValuesAction.create());

                const OscillometryRepeatabilityValues = await legacyClient.calculateOscillometryRepeatability(acceptabilityState.spirometryResults.TestResults);
                const OscillometryBestTestValues = await legacyClient.calculateOscillometryBestTests(acceptabilityState.spirometryResults.TestResults);

                acceptabilityDispatch(UpdateBestTestAction.create(OscillometryBestTestValues));
                acceptabilityDispatch(UpdateRepeatabilityAction.create(OscillometryRepeatabilityValues));

                acceptabilityDispatch(DoneLoadingBestValuesAction.create());
            }
        }

        updateBestTestAndRepeatability();
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch]);

    if (analysisSet === null || sessionMetadata === null || sessionNotes === null) {
        return (<LoadingWithMessage message="Loading Oscillometry Data..." />);
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentState.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentState.testFixedComments.map(x => ({
            TestId: x.id, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({ TestId: x.TestId, Accepted: x.OverreaderAcceptability ? true : false } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateAcceptabilityOscillometry(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            GoBackToList(history, currentUserPermissions, "/Oscillometry", PermissionEnum.OverreadOscillometrySession, "/Oscillometry/Analysis");
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const addNewNote = async (annotation: string) => {
        await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const overreadingDisabled = fixedCommentState.areAvailableFixedCommentsLoaded === false || fixedCommentState.areFixedCommentsLoaded === false;
    const hasAddingNotePermission = currentUserPermissions.some(x=>x.Id == PermissionEnum.AddOscillometrySessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewOscillometrySessionNotes);

    const isQCAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === match.params.analysisSetId).Type.Id === OscillometryAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === match.params.analysisSetId).Type.Id === OscillometryAnalysisSetTypeEnum.Site;

    return (
        <>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            <h2 className="text-primary">Oscillometry Data</h2>
            {!isQCAnalysisSet ? <SessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={analysisSetId}
            /> : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <OscillometryResultsTable
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        resultData={acceptabilityState.spirometryResults}
                        fixedCommentsState={fixedCommentState}
                        fixedCommentsDispatch={fixedCommentDispatch}
                        shouldValidateFixedComments={shouldValidateFixedComments}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch} isRejected={isAnalysisRejected}
                    />
                </div>
            </div>
            <h2 className="text-primary">Attachments</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <FileAttachmentsTable
                        legacyClient={legacyClient}
                        fileAttachments={analysisSet.Attachments.map(a => ({ id: a.Id, fileSizeInBytes: a.FileSizeInBytes, fileName: a.FileName }))}
                        mode={FileAttachmentsTableMode.ListOnly}
                    />
                </div>
            </div>
            {
                hasViewNotePermission?
                <NotesPanel
                header="Session Notes"
                showNotesCount={true}
                isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission}
                notes={sessionNotes}
                onAddNote={(annotation) => addNewNote(annotation)} 
                />
                : null
            }
            <AcceptabilityDashboard
                isViewOnly={isViewOnly || overreadingDisabled}
                analysisSetId={analysisSetId}
                fixedCommentsDispatch={fixedCommentDispatch}
                fixedCommentState={fixedCommentState}
                acceptabilityState={acceptabilityState}
                sessionType={SessionTypes.Oscillometry}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatch(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={() => GoBackToList(history, currentUserPermissions, "/Oscillometry", PermissionEnum.OverreadOscillometrySession, "/Oscillometry/Analysis")}
            />
        </>
    );
};

